import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, PendingImage, PipelineStage } from '../../store/types.ts';
import { STAGE_LABELS, STARTER_TEMPLATES } from '../../store/types.ts';
import { DiagramTypeSelector } from './DiagramTypeSelector.tsx';

const SLASH_COMMANDS = [
  { cmd: '/flowchart', desc: 'Generate a flowchart', fill: 'Create a flowchart that shows ' },
  { cmd: '/sequence', desc: 'Generate a sequence diagram', fill: 'Create a sequence diagram that shows ' },
  { cmd: '/class', desc: 'Generate a class diagram', fill: 'Create a class diagram for ' },
  { cmd: '/er', desc: 'Generate an ER diagram', fill: 'Create an ER diagram for ' },
  { cmd: '/state', desc: 'Generate a state diagram', fill: 'Create a state diagram for ' },
  { cmd: '/gantt', desc: 'Generate a Gantt chart', fill: 'Create a Gantt chart for ' },
  { cmd: '/mindmap', desc: 'Generate a mind map', fill: 'Create a mind map about ' },
  { cmd: '/pie', desc: 'Generate a pie chart', fill: 'Create a pie chart showing ' },
  { cmd: '/architecture', desc: 'Generate an architecture diagram', fill: 'Create an architecture diagram for ' },
  { cmd: '/git', desc: 'Generate a Git graph', fill: 'Create a Git graph showing ' },
  { cmd: '/fix', desc: 'Auto-fix current syntax errors', fill: '__action:fix' },
  { cmd: '/clear', desc: 'Clear chat history', fill: '__action:clear' },
  { cmd: '/retry', desc: 'Retry last request', fill: '__action:retry' },
];

type ChatViewProps = {
  chat: ChatMessage[];
  isSending: boolean;
  pipelineStage: PipelineStage;
  pendingImage: PendingImage | null;
  promptText: string;
  hasLastRequest: boolean;
  onPromptChange: (text: string) => void;
  onSend: () => void;
  onRetry: () => void;
  onClearChat: () => void;
  onStop: () => void;
  onAutoFix: () => void;
  onAttachImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: () => void;
  promptRef: React.RefObject<HTMLTextAreaElement | null>;
  onDiagramTypePrefix: (prefix: string) => void;
};

export function ChatView({
  chat,
  isSending,
  pipelineStage,
  pendingImage,
  promptText,
  hasLastRequest,
  onPromptChange,
  onSend,
  onRetry,
  onClearChat,
  onStop,
  onAutoFix,
  onAttachImage,
  onRemoveImage,
  promptRef,
  onDiagramTypePrefix,
}: ChatViewProps) {
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [slashIdx, setSlashIdx] = useState(0);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat.length, isSending]);

  // Filter slash commands based on current input
  const slashMatches = useMemo(() => {
    if (!promptText.startsWith('/')) return [];
    const query = promptText.toLowerCase();
    return SLASH_COMMANDS.filter((c) => c.cmd.startsWith(query));
  }, [promptText]);

  const showSlash = slashMatches.length > 0 && promptText.startsWith('/');

  const executeSlash = useCallback((fill: string) => {
    if (fill === '__action:fix') {
      onAutoFix();
      onPromptChange('');
    } else if (fill === '__action:clear') {
      onClearChat();
      onPromptChange('');
    } else if (fill === '__action:retry') {
      onRetry();
      onPromptChange('');
    } else {
      onPromptChange(fill);
      promptRef.current?.focus();
    }
  }, [onAutoFix, onClearChat, onRetry, onPromptChange, promptRef]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Chat head */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid var(--ds-border)' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onClearChat}>
            Clear
          </button>
          <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onRetry} disabled={!hasLastRequest || isSending}>
            Retry
          </button>
        </div>
      </div>

      {/* Chat log */}
      <div className="ds-chat" aria-live="polite">
        {!chat.length && (
          <div className="ds-chat__welcome">
            <h3>Describe it. Watch it come alive.</h3>
            <p>Describe a diagram and let AI handle the rest. Iterate quickly with revision history.</p>
            <div className="ds-chat__chips">
              {STARTER_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className="ds-chip"
                  onClick={() => {
                    onPromptChange(template.prompt);
                    promptRef.current?.focus();
                  }}
                >
                  {template.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {chat.map((message) => {
          const stepsSplit = message.text.split('\n---steps---\n');
          const mainText = stepsSplit[0];
          const steps = stepsSplit[1]?.split('\n').filter(Boolean) || [];

          return (
            <article key={message.id} className={`ds-msg ds-msg--${message.role}`}>
              <div className="ds-msg__bubble">
                {mainText}
                {steps.length > 0 && (
                  <div className="ds-msg__steps">
                    {steps.map((step, i) => (
                      <div key={i} className="ds-msg__step">
                        <span className="ds-msg__step-icon">{i + 1}</span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <time className="ds-msg__time">{new Date(message.createdAt).toLocaleTimeString()}</time>
            </article>
          );
        })}

        {isSending && (
          <article className="ds-msg ds-msg--assistant">
            <div className="ds-msg__bubble">{STAGE_LABELS[pipelineStage]}</div>
          </article>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Pending image */}
      {pendingImage && (
        <div className="ds-pending-image" style={{ margin: '0 12px 8px' }}>
          <img src={pendingImage.dataUrl} alt={pendingImage.name} />
          <div className="ds-pending-image__info">
            <div className="ds-pending-image__name">{pendingImage.name}</div>
            <button type="button" className="ds-btn ds-btn--sm ds-btn--danger" onClick={onRemoveImage}>
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Diagram type pills */}
      {!promptText && <DiagramTypeSelector onSelectType={onDiagramTypePrefix} />}

      {/* Slash command dropdown */}
      {showSlash && (
        <div className="ds-slash-menu">
          {slashMatches.map((cmd, i) => (
            <button
              key={cmd.cmd}
              type="button"
              className={`ds-slash-menu__item ${i === slashIdx ? 'ds-slash-menu__item--selected' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); executeSlash(cmd.fill); }}
              onMouseEnter={() => setSlashIdx(i)}
            >
              <span className="ds-slash-menu__cmd">{cmd.cmd}</span>
              <span className="ds-slash-menu__desc">{cmd.desc}</span>
            </button>
          ))}
        </div>
      )}

      {/* Compose area */}
      <div className="ds-compose">
        <div className="ds-compose__controls">
          <label className="ds-attach">
            Attach
            <input type="file" accept="image/*" onChange={onAttachImage} />
          </label>
          {isSending && (
            <button type="button" className="ds-btn ds-btn--sm ds-btn--danger" onClick={onStop}>
              Cancel
            </button>
          )}
        </div>

        <textarea
          ref={promptRef}
          className="ds-compose__textarea"
          value={promptText}
          onChange={(e) => { onPromptChange(e.target.value); setSlashIdx(0); }}
          onKeyDown={(e) => {
            if (!showSlash) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSlashIdx((prev) => (prev + 1) % slashMatches.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSlashIdx((prev) => (prev - 1 + slashMatches.length) % slashMatches.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              const match = slashMatches[slashIdx];
              if (match) executeSlash(match.fill);
            } else if (e.key === 'Escape') {
              onPromptChange('');
            }
          }}
          placeholder="Describe a diagram or type / for commands..."
          rows={3}
        />

        <button
          type="button"
          className="ds-btn ds-btn--primary ds-compose__send"
          onClick={onSend}
          disabled={isSending}
        >
          {isSending ? STAGE_LABELS[pipelineStage] : 'Send ⌘↵'}
        </button>
      </div>
    </div>
  );
}
